import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PublicNavBar } from '../components/PublicNavBar'
import { PublicFooter } from '../components/PublicFooter'
import { FaqSection } from '../components/FaqSection'
import { usePageMeta } from '../hooks/usePageMeta'
import { pageMeta } from '../seo/pageMeta'

interface ProductSection {
  title: string
  subtitle: string
  description: string
  bullets: string[]
  image: string
  imageAlt: string
  cta?: { label: string; to: string }
}

const SECTIONS: ProductSection[] = [
  {
    title: 'Agent Swarm',
    subtitle: 'Your AI engineering team',
    description:
      'Describe a feature and Factory decomposes it into parallel tasks. Up to 5 AI agents work simultaneously, each on its own isolated git branch. No merge conflicts, no stepping on toes — just parallel output.',
    bullets: [
      'AI decomposes features into subtasks automatically',
      'Each agent works in an isolated git worktree',
      'Up to 5 agents running in parallel per project',
      'Smart retry with feedback when reviews fail',
    ],
    image: '/images/products/agent-swarm.png',
    imageAlt: 'Agent panel showing multiple tasks running in parallel',
  },
  {
    title: 'AI Code Review',
    subtitle: 'Every PR reviewed by 3 AI experts',
    description:
      'When an agent finishes, three concurrent AI reviewers check the code for architecture quality, security vulnerabilities, and style issues. Failed reviews trigger automatic retry — the agent gets the feedback and tries again.',
    bullets: [
      'Architecture, security, and style reviews run concurrently',
      'Verdicts: approve, request changes, or comment',
      'Auto-retry on rejection with review feedback injected',
      'GitHub Check Runs and PR comments via GitHub App',
    ],
    image: '/images/products/code-review.png',
    imageAlt: 'Review verdicts showing architecture, security, and style checks',
  },
  {
    title: 'PRD Execution',
    subtitle: 'From spec to shipped, automatically',
    description:
      'Write a PRD, hit execute, and Factory turns every task into a parallel swarm job. Track progress per-PRD with grouped task cards, status breakdowns, and review verdicts. Your spec becomes working code without copy-pasting tickets into an AI.',
    bullets: [
      'One-click execution of all PRD tasks via agent swarm',
      'Grouped progress view: per-PRD task counts and status bars',
      'Each task gets AI code review before merging',
      'ROI tracking: see time and cost saved per execution',
    ],
    image: '/images/products/prd-execution.png',
    imageAlt: 'PRD Executions tab showing grouped tasks with progress bars',
  },
  {
    title: 'GitHub Integration',
    subtitle: 'Works where you already work',
    description:
      'Import any GitHub repo in one click. Label an issue with "factory" and agents start working automatically. Get PRs back on your repo with reviewed, tested code. Factory meets you where you already are.',
    bullets: [
      'One-click GitHub repo import',
      'Label an issue "factory" to trigger orchestration',
      'Automatic PR creation with reviewed code',
      'Issue cards surface GitHub issues as orchestration prompts',
    ],
    image: '/images/products/github-integration.png',
    imageAlt: 'GitHub issues being used as orchestration prompts',
  },
  {
    title: 'Workflows & Pipeline Studio',
    subtitle: 'Visual CI/CD, built in',
    description:
      'A full visual pipeline editor for building multi-stage workflows. Define stages with gates (manual approval, scheduled, API callback, conditional), run action steps in Docker containers, and monitor execution in real time.',
    bullets: [
      'Visual YAML pipeline editor with drag-and-drop',
      '4 gate types: manual, scheduled, API callback, conditional',
      '7 action step types: bash, Python, Node, Go, Rust, curl, custom',
      'Docker-based test and build runners for 12 languages',
    ],
    image: '/images/products/pipeline-studio.png',
    imageAlt: 'Pipeline Studio visual graph editor',
  },
  {
    title: 'Idea Estimator',
    subtitle: 'Know before you build',
    description:
      'Paste any project idea and get a detailed AI-generated technical estimate in seconds. Tech stack recommendations, complexity scoring, component breakdown, and a timeline comparison between solo development and Factory agents.',
    bullets: [
      'AI-powered tech stack recommendations',
      'Complexity score with reasoning (1-10 scale)',
      'Component breakdown with effort estimates',
      'Solo dev timeline vs Factory agents comparison',
    ],
    image: '/images/products/idea-estimator.png',
    imageAlt: 'Estimate card showing tech stack and complexity breakdown',
    cta: { label: 'Try the Estimator', to: '/estimate' },
  },
]

function ScreenshotPlaceholder({ section, index }: { section: ProductSection; index: number }) {
  const colors = [
    'from-primary-500/10 to-primary-600/5 border-primary-200 dark:border-primary-800',
    'from-green-500/10 to-green-600/5 border-green-200 dark:border-green-800',
    'from-orange-500/10 to-orange-600/5 border-orange-200 dark:border-orange-800',
    'from-purple-500/10 to-purple-600/5 border-purple-200 dark:border-purple-800',
    'from-blue-500/10 to-blue-600/5 border-blue-200 dark:border-blue-800',
    'from-rose-500/10 to-rose-600/5 border-rose-200 dark:border-rose-800',
  ]

  return (
    <div className={`aspect-[3/2] rounded-xl border bg-gradient-to-br ${colors[index % colors.length]} flex items-center justify-center`}>
      <div className="text-center px-6">
        <div className="w-12 h-12 mx-auto mb-3 rounded-lg bg-white/60 dark:bg-gray-800/60 flex items-center justify-center">
          <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
          </svg>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{section.imageAlt}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">1200 x 800px</p>
      </div>
    </div>
  )
}

function ProductImage({ section, index }: { section: ProductSection; index: number }) {
  const [failed, setFailed] = useState(false)

  if (failed) {
    return <ScreenshotPlaceholder section={section} index={index} />
  }

  return (
    <img
      src={section.image}
      alt={section.imageAlt}
      className="rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg w-full"
      onError={() => setFailed(true)}
    />
  )
}

export default function ProductsPage() {
  usePageMeta(pageMeta('/products'))

  return (
    <div className="min-h-screen text-gray-900 dark:text-gray-100">
      <PublicNavBar />

      {/* Hero */}
      <section className="px-4 pt-16 sm:pt-24 pb-12 sm:pb-16">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl sm:text-5xl font-bold mb-4 leading-tight">
            <span className="bg-gradient-to-r from-gray-900 via-gray-700 to-gray-500 dark:from-white dark:via-gray-200 dark:to-gray-400 bg-clip-text text-transparent">
              AI Coding Agent Features: Agent Swarm, AI Code Review &amp; PRD Execution
            </span>
          </h1>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            Everything Factory Nexus does: an AI engineering team that decomposes features, writes code in
            parallel, reviews every PR, and merges to your repo. Here&apos;s how each piece works.
          </p>
        </div>
      </section>

      {/* Capability Sections */}
      {SECTIONS.map((section, i) => {
        const isReversed = i % 2 === 1
        return (
          <section
            key={section.title}
            className={`py-12 sm:py-20 px-4 ${i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/50' : ''}`}
          >
            <div className="max-w-6xl mx-auto">
              <div className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center ${isReversed ? 'lg:direction-rtl' : ''}`}>
                {/* Text side */}
                <div className={isReversed ? 'lg:order-2' : ''}>
                  <p className="text-sm font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-wider mb-2">
                    {section.subtitle}
                  </p>
                  <h2 className="text-2xl sm:text-3xl font-bold mb-4">{section.title}</h2>
                  <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                    {section.description}
                  </p>
                  <ul className="space-y-3 mb-6">
                    {section.bullets.map((bullet, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-green-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <span className="text-sm text-gray-600 dark:text-gray-400">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  {section.cta && (
                    <Link
                      to={section.cta.to}
                      className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 transition-colors"
                    >
                      {section.cta.label}
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                    </Link>
                  )}
                </div>

                {/* Image side */}
                <div className={isReversed ? 'lg:order-1' : ''}>
                  <ProductImage section={section} index={i} />
                </div>
              </div>
            </div>
          </section>
        )
      })}

      <FaqSection path="/products" />

      {/* Bottom CTA */}
      <section className="py-16 sm:py-24 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Ready to ship faster?
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">
            Describe what you need. Your AI team handles the rest.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="px-6 py-3 bg-gradient-to-r from-primary-600 to-orange-500 text-white font-medium rounded-lg hover:from-primary-500 hover:to-orange-400 transition-all shadow-lg shadow-orange-500/20"
            >
              Try it now
            </Link>
            <Link
              to="/estimate"
              className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 font-medium rounded-lg hover:border-gray-400 hover:text-gray-900 dark:hover:text-white transition-all"
            >
              Estimate an idea
            </Link>
            <Link
              to="/register"
              className="px-6 py-3 text-gray-500 dark:text-gray-400 font-medium rounded-lg hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              Get started free
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}
